import {first} from 'lodash';
import transparentize from 'polished/lib/color/transparentize';
import React, {FC} from 'react';
import styled from 'styled-components';

import {MessageAnnotationModel} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {searchableAttrsBody} from '../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {preventDragStart} from '../../../../../helpers/dragAndDropHelpers';
import {markFullStorySensitive} from '../../../../../helpers/fullstoryHelpers';
import {MessageViewerAttachmentsIcon} from './messageViewerAttachmentsIcon';
import {MessageViewerAvatar} from './messageViewerAvatar';
import {MessageViewerDate, MessageViewerDateProps} from './messageViewerDate';
import {MessageViewerSeen} from './messageViewerSeen';
import {MessageViewerSender, MessageViewerSenderProps} from './sender/messageViewerSender';

/*
 * Props.
 */

interface MessageViewerCollapsedProps extends MessageViewerDateProps, MessageViewerSenderProps {
  message: MessageModel;
  annotations?: ReadonlyArray<MessageAnnotationModel>;
  onExpandClick: () => void;
  isChatUserOnline?: boolean;
}

/*
 * Style.
 */

const StyledCollapsedButton = styled(ChromelessButton)`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto auto auto;
  grid-template-areas: 'avatar middle seen attachments date';
  padding: 8px 10px 8px 9px;

  & > * {
    pointer-events: none;
  }
`;

const StyledMiddleDiv = styled.div`
  grid-area: middle;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0 7px;
`;

const StyledSenderDiv = styled.div``;

const StyledBlurbDiv = styled.div`
  line-height: 20px;
  color: ${(p) => p.theme.greys.shade60};
  ${ellipsis()};

  mark {
    color: ${(p) => p.theme.greys.shade60};
    font-weight: ${fontWeights.medium};
    background-color: ${transparentize(0.8, '#FF5C00')} !important;
  }
`;

const StyledWrapperDiv = styled.div`
  grid-area: seen;
  margin: 0 7px;
`;

/*
 * Component.
 */

export const MessageViewerCollapsed: FC<MessageViewerCollapsedProps> = (props) => (
  <StyledCollapsedButton onClick={props.onExpandClick}>
    <MessageViewerAvatar
      panelId={props.panelId}
      message={props.message}
      author={props.author || first(props.visualRecipients.from)}
      isChatUserOnline={props.isChatUserOnline}
    />
    <StyledMiddleDiv>
      <StyledSenderDiv>
        <MessageViewerSender
          searchQuery={props.searchQuery}
          shouldHideClickableUntilHover
          panelId={props.panelId}
          message={props.message}
          author={props.author}
          widgetCompanyName={props.widgetCompanyName}
          visualRecipients={props.visualRecipients}
          activitySourceVisualType={props.activitySourceVisualType}
          isMessageViewerExpanded={props.isMessageViewerExpanded}
          isClickable={props.isClickable}
          needsParentHover={props.needsParentHover}
          scrollerVisibilityRef={props.scrollerVisibilityRef}
          isNewUnread={props.isNewUnread}
          isUserAuthor={props.isUserAuthor}
        />
      </StyledSenderDiv>
      <StyledBlurbDiv className={markFullStorySensitive()} {...searchableAttrsBody}>
        {renderMessageSummary(props)}
      </StyledBlurbDiv>
    </StyledMiddleDiv>
    {renderSeenOrSharedBy(props)}
    <MessageViewerAttachmentsIcon attachments={props.message.attachments} />
    <MessageViewerDate
      message={props.message}
      isInDragRegion={props.isInDragRegion}
      timelineVariation={props.timelineVariation}
    />
  </StyledCollapsedButton>
);

function renderSeenOrSharedBy(props: MessageViewerCollapsedProps) {
  return (
    <StyledWrapperDiv draggable={props.isInDragRegion} onDragStart={preventDragStart}>
      <MessageViewerSeen message={props.message} />
    </StyledWrapperDiv>
  );
}

function renderMessageSummary(props: MessageViewerCollapsedProps) {
  return props.message.blurb;
}
